import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext.jsx';
import BranchBadge from './BranchBadge.jsx';

const LOGO = '/brand/logo-224.webp';

/**
 * Brand header (BRAND.md section 10.3): badge at 80px, three-line green
 * tagline, right-aligned nav with a green underline on the active item, and
 * one green primary button. Sticky, white, 1px light-gray bottom border.
 *
 * links: [{ to, label, end }] for router links, or [{ hash, label }] for
 * in-page anchors on the home page.
 */
export default function BrandHeader({ links, homeTo = '/' }) {
  const { user, profile, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const isHome = location.pathname === '/';

  useEffect(() => { setOpen(false); }, [location.pathname, location.hash]);

  return (
    <header className={`site-header${open ? ' nav-open' : ''}`}>
      <a className="skip-link" href="#main">Skip to content</a>
      <div className="site-header-inner">
        <Link to={homeTo} className="brand-link" aria-label="VetTech Learning Hub home">
          <img className="brand-badge" src={LOGO} width="80" height="80" alt="" />
          <span className="brand-tagline">
            <span>Training Veterans.</span>
            <span>Building Careers.</span>
            <span>Free Cert Prep.</span>
          </span>
        </Link>

        <nav id="site-nav" className="site-nav" aria-label="Primary">
          {links.map((l) => (
            l.hash ? (
              <a key={l.label} href={isHome ? l.hash : `/${l.hash}`}>{l.label}</a>
            ) : (
              <NavLink key={l.label} to={l.to} end={!!l.end} className={({ isActive }) => (isActive ? 'active' : undefined)}>
                {l.label}
              </NavLink>
            )
          ))}
          {user && (
            <button type="button" className="nav-link-btn" onClick={logout}>Sign Out</button>
          )}
        </nav>

        <div className="site-actions">
          {profile?.branch && (
            <BranchBadge branchKey={profile.branch} statusKey={profile.status} size={40} />
          )}
          {user ? (
            <Link to="/profile" className="btn">Profile <ArrowRight aria-hidden="true" /></Link>
          ) : (
            <Link to="/signin" className="btn">Sign In / Sign Up <ArrowRight aria-hidden="true" /></Link>
          )}
        </div>

        <button
          type="button"
          className="nav-toggle"
          aria-expanded={open}
          aria-controls="site-nav"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          Menu
        </button>
      </div>
    </header>
  );
}